import { useFormik } from 'formik';
import * as Yup from 'yup';
import { KeenIcon } from '@/components';
import { useAuthContext } from '@/auth';
import { useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLayout } from '@/providers';
import FormInput from '@/components/ui/input/FormInput';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { changePassword } from '@/api/UserApi';
import BtnSubmitAuth from '../../../../components/ui/btnSubmitAuth';

const ResetPasswordChange = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Lấy email và otp từ state
  const { email, otp } = location.state || {};

  const passwordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(6, intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.MIN_LENGTH' }, { min: 6 }))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])/, intl.formatMessage({ id: 'AUTH.ERROR.PASSWORD_COMPLEXITY' }))
      .required(intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.REQUIRED' })),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.MATCH' }))
      .required(intl.formatMessage({ id: 'AUTH.RESET_PASS.VALIDATION.CONFIRM_REQUIRED' }))
  });

  const {
    currentLayout
  } = useLayout();
  const {
    changePassword: authChangePassword
  } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      setLoading(true);
      setStatus(undefined);
      try {
        // Gọi API change password với đầy đủ payload
        const response = await changePassword({
          email,
          otp,
          password: values.newPassword,
          password_confirmation: values.confirmPassword
        });

        const payload = response && typeof response === 'object' && 'data' in response ? response.data : response;
        const apiStatus = payload && typeof payload.status === 'number' ? payload.status : undefined;
        const apiMessage = payload && payload.message ? payload.message : undefined;

        if (apiStatus === 200 || /success/i.test(apiMessage || '')) {
          toast.success(intl.formatMessage({ id: 'AUTH.RESET_PASS.CHANGE_SUCCESS' }));
          navigate(currentLayout?.name === 'auth-branded' ? '/auth/reset-password/changed' : '/auth/classic/reset-password/changed');
        } else if (apiStatus === 404) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.ACCOUNT_NOT_FOUND', defaultMessage: 'Không tìm thấy tài khoản!' }));
        } else if (apiStatus === 422) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.VALIDATION_FAILED' }));
        } else if (apiStatus === 400) {
          // Likely invalid/expired OTP
          setStatus(intl.formatMessage({ id: 'AUTH.OTP.INVALID_OR_EXPIRED', defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!' }));
        } else if (typeof apiStatus === 'number') {
          setStatus(apiMessage || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        } else {
          // No status - infer from message
          if (/invalid|expired/i.test(apiMessage || '')) {
            setStatus(intl.formatMessage({ id: 'AUTH.OTP.INVALID_OR_EXPIRED', defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!' }));
          } else {
            setStatus(apiMessage || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          }
        }
      } catch (error) {
        if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || error?.code === 'ECONNABORTED')) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.NETWORK' }));
        } else if (error?.response) {
          const status = error.response.status;
          const data = error.response.data || {};
          if (status === 404) {
            setStatus(intl.formatMessage({ id: 'AUTH.ERROR.ACCOUNT_NOT_FOUND', defaultMessage: 'Không tìm thấy tài khoản!' }));
          } else if (status === 422) {
            setStatus(intl.formatMessage({ id: 'AUTH.ERROR.VALIDATION_FAILED' }));
          } else if (status === 400) {
            setStatus(intl.formatMessage({ id: 'AUTH.OTP.INVALID_OR_EXPIRED', defaultMessage: 'OTP không hợp lệ hoặc đã hết hạn!' }));
          } else {
            setStatus(data?.message || data?.error || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          }
        } else {
          setStatus(error.message || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        }
      }
      setLoading(false);
      setSubmitting(false);
    }
  });

  return <div className="card max-w-[370px] w-full">
      <form className="card-body flex flex-col gap-5 p-10" onSubmit={formik.handleSubmit} noValidate>
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900"><FormattedMessage id="AUTH.RESET_PASS.TITLE" /></h3>
          <span className="text-2sm text-gray-700"><FormattedMessage id="AUTH.RESET_PASS.PLACEHOLDER" /></span>
        </div>

        <div className="flex flex-col gap-1">
          <FormInput
            type="password"
            label={<FormattedMessage id="AUTH.RESET_PASS.NEW_PASSWORD" />}
            name="newPassword"
            icon= {<KeenIcon icon="lock-2" className="text-lg" />}
            placeholder={intl.formatMessage({ id: 'AUTH.RESET_PASS.PLACEHOLDER' })}
            autoComplete="off"
            error={formik.errors.newPassword}
            touched={formik.touched.newPassword}
            {...formik.getFieldProps('newPassword')}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FormInput
            type="password"
            label={<FormattedMessage id="AUTH.RESET_PASS.CONFIRM_PASSWORD" />}
            name="confirmPassword"
            icon= {<KeenIcon icon="key" className="text-lg" />}
            placeholder={intl.formatMessage({id: 'AUTH.RESET_PASS.CONFIRM_PASSWORD_PLACEHOLDER'})}
            autoComplete="off"
            error={formik.errors.confirmPassword}
            touched={formik.touched.confirmPassword}
            {...formik.getFieldProps('confirmPassword')}
          />
        </div>

        {formik.status && (
          <div className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {formik.status}
          </div>
        )}

        <BtnSubmitAuth
          loading={loading}
          disabled={loading}
        >
          {loading ? <FormattedMessage id="AUTH.PLEASE_WAIT" /> : <FormattedMessage id="AUTH.RESET_PASS.SUBMIT" />}
        </BtnSubmitAuth>
      </form>
    </div>;
};

export { ResetPasswordChange };